'use client';

import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckIcon, ErrorIcon, WarningIcon, InfoIcon } from './icons/IconSystem';
import LoadingSpinner from './LoadingSpinner';
import { OVERLAY_Z_INDEX } from '@/lib/constants';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  confirmText?: string;
  showCancel?: boolean;
  cancelText?: string;
  onConfirm?: () => void;
  autoClose?: boolean;
  autoCloseDelay?: number;
  children?: React.ReactNode;
  maxWidth?: string;
  isLoading?: boolean;
  result?: 'success' | 'error' | null;
  resultMessage?: string;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  confirmText,
  showCancel = false,
  cancelText = '取消',
  onConfirm,
  autoClose,
  autoCloseDelay = 3000,
  children,
  maxWidth,
  isLoading = false,
  result = null,
  resultMessage
}) => {
  // 自动关闭逻辑
  useEffect(() => {
    if (isOpen && autoClose && autoCloseDelay) {
      const timer = setTimeout(() => {
        onClose();
      }, autoCloseDelay);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, autoClose, autoCloseDelay, onClose]);

  // 键盘事件处理：焦点在输入框时禁用回车提交，避免未输入完就误触
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen || isLoading || result) return;

      if (event.key === 'Escape') {
        onClose();
      } else if (event.key === 'Enter' && onConfirm && !isLoading) {
        const tag = document.activeElement?.tagName;
        if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag ?? '')) return;
        onConfirm();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose, onConfirm, isLoading, result]);

  // 点击遮罩层关闭（加载时或显示结果时不允许关闭）
  const handleOverlayClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget && !isLoading && !result) {
      onClose();
    }
  };

  // 获取类型相关的样式
  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          icon: <CheckIcon size={24} color="#2ECC71" />,
          iconColor: '#2ECC71',
          bgColor: '#F0FFF4',
          borderColor: '#2ECC71'
        };
      case 'error':
        return {
          icon: <ErrorIcon size={24} color="#FF4C4C" />,
          iconColor: '#FF4C4C',
          bgColor: '#FFF5F5',
          borderColor: '#FF4C4C'
        };
      case 'warning':
        return {
          icon: <WarningIcon size={24} color="#F59E0B" />,
          iconColor: '#F59E0B',
          bgColor: '#FFFBEB',
          borderColor: '#F59E0B'
        };
      case 'info':
      default:
        return {
          icon: <InfoIcon size={24} color="#3B82F6" />,
          iconColor: '#3B82F6',
          bgColor: '#EFF6FF',
          borderColor: '#3B82F6'
        };
    }
  };

  const typeStyles = getTypeStyles();

  // 如果显示结果状态，使用结果类型
  const displayType = result === 'success' ? 'success' : result === 'error' ? 'error' : type;
  const displayTypeStyles = result === 'success' ? {
    icon: <CheckIcon size={24} color="#2ECC71" />,
    iconColor: '#2ECC71',
    bgColor: '#F0FFF4',
    borderColor: '#2ECC71'
  } : result === 'error' ? {
    icon: <ErrorIcon size={24} color="#FF4C4C" />,
    iconColor: '#FF4C4C',
    bgColor: '#FFF5F5',
    borderColor: '#FF4C4C'
  } : typeStyles;

  if (!isOpen) return null;

  const overlayContent = result ? (
    <div
      className="modal-overlay"
      onClick={handleOverlayClick}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: OVERLAY_Z_INDEX,
          padding: '1rem',
          backdropFilter: 'blur(4px)',
          animation: 'fadeIn 0.2s ease-out'
        }}
      >
        <div
          className="modal-content"
          style={{
            backgroundColor: '#FFFFFF',
            borderRadius: '16px',
            padding: '2rem',
            maxWidth: maxWidth || '500px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            animation: 'slideIn 0.3s ease-out',
            position: 'relative',
            overflow: 'visible',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}
        >
          {/* 结果图标 */}
          <div
            style={{
              width: '64px',
              height: '64px',
              backgroundColor: result === 'success' ? '#10b981' : '#ef4444',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              fontSize: '32px',
              color: '#ffffff',
              fontWeight: '600'
            }}
          >
            {result === 'success' ? '✓' : '✕'}
          </div>
          
          {/* 结果标题 */}
          <h3
            style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: result === 'success' ? '#059669' : '#dc2626',
              margin: '0 0 0.5rem 0',
              textAlign: 'center'
            }}
          >
            {result === 'success' ? '操作成功！' : '操作失败'}
          </h3>
          
          {/* 结果描述 */}
          <p
            style={{
              fontSize: '0.875rem',
              color: result === 'success' ? '#047857' : '#991b1b',
              margin: '0 0 2rem 0',
              textAlign: 'center',
              lineHeight: '1.6'
            }}
          >
            {resultMessage || message}
          </p>
          
          {/* 完成按钮 */}
          {result === 'success' && (
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 2rem',
                backgroundColor: '#000000',
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
                display: 'block',
                margin: '0 auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              完成
            </button>
          )}
          
          {/* 错误时的关闭按钮 */}
          {result === 'error' && (
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 2rem',
                backgroundColor: 'transparent',
                color: '#6b7280',
                border: '1px solid #E5E5E5',
                borderRadius: '6px',
                fontSize: '0.875rem',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'opacity 0.2s ease',
                display: 'block',
                margin: '0 auto'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.8';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.opacity = '1';
              }}
            >
              关闭
            </button>
          )}
        </div>
      </div>
  ) : (
    <div
      className="modal-overlay"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: OVERLAY_Z_INDEX,
        padding: '1rem',
        backdropFilter: 'blur(4px)',
        animation: 'fadeIn 0.2s ease-out'
      }}
    >
      <div
        className="modal-content"
        style={{
          backgroundColor: '#FFFFFF',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: maxWidth || '500px',
          width: '100%',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          animation: 'slideIn 0.3s ease-out',
          position: 'relative',
          overflow: 'visible',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* 类型图标或加载指示器 - 只在没有children时显示 */}
        {!children && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem'
            }}
          >
            {isLoading ? (
              <LoadingSpinner size="medium" showText={false} color="#000000" />
            ) : (
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: typeStyles.bgColor,
                  border: `2px solid ${typeStyles.borderColor}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px',
                  color: typeStyles.iconColor,
                  fontWeight: 'bold'
                }}
              >
                {typeStyles.icon}
              </div>
            )}
          </div>
        )}

        {/* 标题 */}
        {title && (
          <h3
            style={{
              fontSize: '1.25rem',
              fontWeight: '600',
              color: '#000000',
              marginBottom: '0.75rem',
              textAlign: 'center'
            }}
          >
            {title}
          </h3>
        )}

        {/* 内容区域 */}
        {children ? (
          <div style={{ marginBottom: '0' }}>
            {children}
          </div>
        ) : (
          /* 消息 */
          <div
            style={{
              color: '#666666',
              lineHeight: '1.6',
              marginBottom: '1.5rem',
              textAlign: 'center',
              fontSize: '0.95rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {message}
          </div>
        )}

        {/* 按钮组 - 只在有confirmText且不在加载状态时显示 */}
        {confirmText && !isLoading && (
          <div
            style={{
              display: 'flex',
              gap: '0.75rem',
              justifyContent: 'center',
              flexWrap: 'wrap',
              marginTop: children ? '1.5rem' : 0
            }}
          >
            {showCancel && (
              <button
                onClick={onClose}
                disabled={isLoading}
                style={{
                  backgroundColor: 'transparent',
                  color: isLoading ? '#9CA3AF' : '#666666',
                  border: '1px solid #E5E5E5',
                  borderRadius: '8px',
                  padding: '0.75rem 1.5rem',
                  fontSize: '0.9rem',
                  fontWeight: '500',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '80px',
                  opacity: isLoading ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isLoading) {
                  e.currentTarget.style.backgroundColor = '#F5F5F5';
                  e.currentTarget.style.borderColor = '#CCCCCC';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isLoading) {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#E5E5E5';
                  }
                }}
              >
                {cancelText}
              </button>
            )}
            
            <button
              onClick={onConfirm || onClose}
              disabled={isLoading}
              className="button-primary"
            >
              {isLoading ? (
                <LoadingSpinner size="small" showText={false} color="#FFFFFF" />
              ) : (
                confirmText
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );

  return typeof document !== 'undefined'
    ? createPortal(overlayContent, document.body)
    : null;
};

export default Modal; 